import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { withSiteContext } from '../../interceptors/site-context.interceptor';
import { AccessibleSite, Paged, SiteAccess } from '../../models/team/team';
import { authHeaders } from './team-http';

/** Lo que dura en memoria el acceso de una tienda antes de volver a preguntarlo. */
const ACCESS_TTL_MS = 30_000;

/**
 * El acceso del usuario a cada tienda: el menú, los candados y el guard leen de aquí. Se cachea
 * poco tiempo porque el dueño puede cambiar un rol en cualquier momento; al entrar en una tienda
 * y tras un 403 se vuelve a preguntar.
 */
@Injectable({ providedIn: 'root' })
export class SiteAccessService {

  private readonly cache = new Map<string, { at: number; access$: Observable<SiteAccess> }>();
  private readonly deniedSubject = new Subject<string>();

  /** Tiendas en las que el backend acaba de negar el acceso (rol cambiado o retirado). */
  readonly denied$ = this.deniedSubject.asObservable();

  constructor(private http: HttpClient) {}

  /** Propias y compartidas, ordenadas por nombre. */
  accessible(page: number, size: number, query = ''): Observable<Paged<AccessibleSite>> {
    let params = new HttpParams().set('page', page).set('size', size);
    if (query.trim()) params = params.set('q', query.trim());
    return this.http.get<Paged<AccessibleSite>>(`${environment.baseUrl}admin/sites/accessible`,
      { headers: authHeaders(), params });
  }

  access(siteId: string, fresh = false): Observable<SiteAccess> {
    const cached = this.cache.get(siteId);
    if (!fresh && cached && Date.now() - cached.at < ACCESS_TTL_MS) return cached.access$;
    const access$ = this.http.get<SiteAccess>(`${environment.baseUrl}admin/sites/${encodeURIComponent(siteId)}/access/me`,
      { headers: authHeaders(), context: withSiteContext(siteId) }).pipe(
      tap({ error: () => this.cache.delete(siteId) }),
      shareReplay(1),
    );
    this.cache.set(siteId, { at: Date.now(), access$ });
    return access$;
  }

  permissions(siteId: string, fresh = false): Observable<ReadonlySet<string>> {
    return this.access(siteId, fresh).pipe(map(access => new Set(access.permissions)));
  }

  /**
   * ¿Puede el usuario hacer esto en la tienda? Para enseñar u ocultar una acción: si el acceso no se
   * puede consultar, sí (como el guard); el backend decide igualmente en cada petición.
   */
  can(siteId: string | null | undefined, permission: string): Observable<boolean> {
    if (!siteId) return of(true);
    return this.permissions(siteId).pipe(map(permissions => permissions.has(permission)), catchError(() => of(true)));
  }

  /** El backend negó una petición de esta tienda: lo cacheado ya no vale. */
  markDenied(siteId: string): void {
    this.cache.delete(siteId);
    this.deniedSubject.next(siteId);
  }

  forget(siteId?: string): void {
    siteId ? this.cache.delete(siteId) : this.cache.clear();
  }

  /** Dejar una tienda en la que se es miembro. */
  leave(siteId: string): Observable<void> {
    return this.http.delete<void>(`${environment.baseUrl}admin/team/memberships/${encodeURIComponent(siteId)}`,
      { headers: authHeaders() }).pipe(tap(() => this.forget(siteId)));
  }

}
